import {
  getEnrollment,
  INTAKE_CADENCE_LABEL,
  INTAKE_CADENCE_OR_LABEL,
  isLegacyCampaignPath,
} from './intake.js'
import { buildCampaignWhatsAppUrl, siteConfig } from './siteConfig.js'

export type StaticPageKind = 'core' | 'local' | 'guide' | 'campaign' | 'roadmap' | 'admission'

export type StaticPageItem =
  | string
  | {
      label: string
      href: string
      external?: boolean
    }

export type StaticPageSection = {
  heading: string
  body?: string
  items?: StaticPageItem[]
  tone?: 'notice'
}

export type StaticPageFaq = {
  question: string
  answer: string
}

export type StaticPageCta = {
  label: string
  href: string
}

export type StaticPage = {
  slug: string
  path: string
  kind: StaticPageKind
  title: string
  metaDescription: string
  h1: string
  eyebrow: string
  introAnswer: string
  sections: StaticPageSection[]
  faqs: StaticPageFaq[]
  cta: StaticPageCta
  secondaryCtas?: StaticPageCta[]
  schemaType?: 'AboutPage' | 'ContactPage' | 'CollectionPage' | 'WebPage'
  sitemap: {
    priority: number
    changefreq: 'weekly' | 'monthly'
  }
}

export type StaticPageOptions = {
  prerender?: boolean
}

type IntakePageCopy = {
  campaignTitle: string
  campaignMeta: string
  campaignEyebrow: string
  batchStartItem: string
  primaryCoursesItem: string
  orientationTitle: string
  admissionsMeta: string
  admissionsIntro: string
  seeCampaignCta: string
}

function getIntakePageCopy(now: Date, prerender: boolean): IntakePageCopy {
  if (prerender) {
    return {
      campaignTitle: `${siteConfig.campaign.name} | AI, Full Stack & React Native`,
      campaignMeta: `Join ${siteConfig.campaign.name} for AI Agent Builder, Full Stack Web Development with AI, and React Native + AI. Intakes start in ${INTAKE_CADENCE_LABEL}. Start with a free live career class, roadmap, counselling, and seat booking guidance.`,
      campaignEyebrow: 'Now enrolling · quarterly intake',
      batchStartItem: `Batch start: the next ${INTAKE_CADENCE_OR_LABEL} date. The live page shows the exact start date.`,
      primaryCoursesItem:
        'Primary intake courses: AI Agent Builder, Full Stack Web Development with AI, and React Native + AI.',
      orientationTitle: 'Free Career Orientation Class | Skillo Academy',
      admissionsMeta: `Read Skillo Academy\u2019s admission policies for referral benefit, founder scholarship, seat booking token, installment support, and honest no-income-guarantee rules.`,
      admissionsIntro: `Skillo Academy\u2019s admission support includes a simple referral benefit, partial founder scholarships, seat booking token, installment support, and a clear no-income-guarantee policy before enrollment.`,
      seeCampaignCta: 'See the Career Launch page',
    }
  }

  const enrollment = getEnrollment(now)
  return {
    campaignTitle: `${siteConfig.campaign.name} | ${enrollment.batchMonth} ${enrollment.batchYear} | AI, Full Stack & React Native`,
    campaignMeta: `Join ${siteConfig.campaign.name} for AI Agent Builder, Full Stack Web Development with AI, and React Native + AI. Next intake: ${enrollment.nextBatchDate}. Start with a free live career class, roadmap, counselling, and seat booking guidance.`,
    campaignEyebrow: `Now enrolling · ${enrollment.batchMonth} ${enrollment.batchYear}`,
    batchStartItem: `Batch start: ${enrollment.nextBatchDate}.`,
    primaryCoursesItem: `Primary ${enrollment.batchMonth} intake courses: AI Agent Builder, Full Stack Web Development with AI, and React Native + AI.`,
    orientationTitle: `Free Career Orientation Class | Skillo Academy ${enrollment.batchLabel}`,
    admissionsMeta: `Read Skillo Academy\u2019s ${enrollment.batchLabel} admission policies for referral benefit, founder scholarship, seat booking token, installment support, and honest no-income-guarantee rules.`,
    admissionsIntro: `Skillo Academy\u2019s ${enrollment.batchLabel} admission support includes a simple referral benefit, partial founder scholarships, seat booking token, installment support, and a clear no-income-guarantee policy before enrollment.`,
    seeCampaignCta: `See ${enrollment.batchLabel} page`,
  }
}

export function getStaticPages(now = new Date(), options: StaticPageOptions = {}): StaticPage[] {
  const prerender = Boolean(options.prerender)
  const enrollment = getEnrollment(now)
  const intakeCopy = getIntakePageCopy(now, prerender)
  const whatsappCounsellingUrl = `https://wa.me/${siteConfig.contact.phoneE164}?text=${encodeURIComponent(
    'Hi, I want free career counselling from Skillo Academy. Please help me choose the right course.',
  )}`

  const aiRoadmapUrl = buildCampaignWhatsAppUrl('ai', now, { includeIntake: !prerender })
  const webRoadmapUrl = buildCampaignWhatsAppUrl('web', now, { includeIntake: !prerender })
  const aiClassUrl = buildCampaignWhatsAppUrl('aiClass', now, { includeIntake: !prerender })

  return [
    {
      slug: 'about',
      path: '/about',
      kind: 'core',
      title: 'About Skillo Academy | Live Tech Academy in Sylhet, Bangladesh',
      metaDescription:
        'Learn about Skillo Academy, a Sylhet-based live tech academy teaching AI Agent Building, Full Stack Web Development, practical digital skills, and computer/English foundations in Bangla + English.',
      h1: 'About Skillo Academy',
      eyebrow: 'Sylhet-based live tech academy',
      introAnswer: siteConfig.brand.entityStatement,
      schemaType: 'AboutPage',
      sitemap: { priority: 0.8, changefreq: 'monthly' },
      sections: [
        {
          heading: 'What Skillo teaches',
          body: 'Skillo focuses on practical digital skills that students can turn into portfolio projects, academic demos, job applications, personal products, and optional freelancing later — depending on their goals and effort.',
          items: [
            'AI Agent Building and AI automation with n8n, APIs, RAG, WhatsApp agents, and voice AI workflows.',
            'Full Stack Web Development with HTML, CSS, Tailwind, JavaScript, TypeScript, React, Next.js, Node.js, PostgreSQL, MongoDB, auth, payments, deployment, and AI integration.',
            'Foundation computer and practical English skills for absolute beginners preparing for technical courses and long-term digital confidence.',
          ],
        },
        {
          heading: 'How classes work',
          items: [
            `Live mentor-led cohorts with a maximum of ${enrollment.seats} students in the main batches.`,
            'Bangla + English teaching so Bangladeshi learners can understand concepts and build technical English fluency.',
            'Real projects, portfolio guidance, WhatsApp support, and honest career direction from admission to graduation.',
          ],
        },
        {
          heading: 'Founder and instructor',
          body: 'Skillo Academy is founded by Minhaj Sadik, a Sylhet-based backend and DevOps engineer who teaches with a production-first mindset. His courses focus on shipping useful projects, not only watching tutorials.',
          items: [
            { label: 'Founder website', href: 'https://minhajsadik.com', external: true },
            { label: 'GitHub', href: 'https://github.com/MinhajSadik', external: true },
            { label: 'LinkedIn', href: 'https://linkedin.com/in/minhajosadik', external: true },
          ],
        },
        {
          heading: 'Location and contact',
          items: [
            `Location: ${siteConfig.contact.location}, Bangladesh.`,
            `WhatsApp: ${siteConfig.contact.phoneDisplay}.`,
            `Email: ${siteConfig.contact.email}.`,
          ],
        },
      ],
      faqs: [
        {
          question: 'What is Skillo Academy?',
          answer:
            'Skillo Academy is a live mentor-led tech academy in Sylhet, Bangladesh, focused on AI Agent Building, Full Stack Web Development, practical digital skills, and foundation computer/English training. It teaches skills and portfolio work — not guaranteed income.',
        },
        {
          question: 'Does Skillo Academy teach in Bangla?',
          answer:
            'Yes. Skillo teaches in Bangla + English so students can understand lessons clearly while becoming comfortable with technical English.',
        },
        {
          question: 'Are Skillo courses online or offline?',
          answer:
            'Skillo is based in Sylhet and runs live mentor-led cohorts with local support. Course pages show the current class format and admission details for each batch.',
        },
      ],
      cta: {
        label: 'Talk to Skillo on WhatsApp',
        href: whatsappCounsellingUrl,
      },
    },
    {
      slug: 'free-career-counselling',
      path: '/free-career-counselling',
      kind: 'core',
      title: 'Free Career Counselling for Tech Courses | Skillo Academy',
      metaDescription:
        'Get free Skillo Academy counselling on whether to start with AI Agent Building, Full Stack Web Development, Basic Computer & English, design, marketing, or another practical tech skill path.',
      h1: 'Free Career Counselling for Tech Learners',
      eyebrow: 'Choose the right course before enrolling',
      introAnswer:
        'Skillo Academy offers free career counselling for students, beginners, and career switchers who want help choosing between AI Agent Building, Full Stack Web Development, Basic Computer & English, design, marketing, and other practical tech courses — with honest expectations and no income guarantees.',
      schemaType: 'ContactPage',
      sitemap: { priority: 0.8, changefreq: 'weekly' },
      sections: [
        {
          heading: 'Who this is for',
          items: [
            'Students after SSC, HSC, diploma, or university who want a practical tech roadmap.',
            'Beginners who are not sure whether to start with Basic Computer, Web Development, AI Agent Builder, design, video editing, or digital marketing.',
            'Career switchers who want a realistic path based on time, laptop access, English level, and current skills.',
          ],
        },
        {
          heading: 'What you get',
          items: [
            'A recommended first course based on your current skill level.',
            'A simple 3-month learning and portfolio plan.',
            'Honest guidance about prerequisites, practice time, and expected difficulty.',
            'Clear next steps for admission, batch timing, and payment options.',
          ],
        },
        {
          heading: 'What we will not promise',
          body: siteConfig.trust.incomeDisclaimer,
        },
      ],
      faqs: [
        {
          question: 'Which Skillo course is best for beginners?',
          answer:
            'Absolute beginners should usually start with Basic Computer & English. Students who already use a computer confidently can choose Full Stack Web Development with AI or AI Agent Builder based on whether they prefer coding or automation workflows.',
        },
        {
          question: 'Can I learn useful tech skills without heavy coding?',
          answer:
            'Yes. Paths like AI automation, design, video editing, digital marketing, and basic computer skills can begin with low-code or no-code tools. Freelancing remains optional and results are never guaranteed. Coding paths like Full Stack Web Development need more practice but open broader technical opportunities.',
        },
        {
          question: 'Do I need a laptop?',
          answer:
            'For most technical courses, a laptop or desktop is strongly recommended. Counselling can help you choose a course based on the device you currently have access to.',
        },
      ],
      cta: {
        label: 'Get free counselling on WhatsApp',
        href: whatsappCounsellingUrl,
      },
    },
    {
      slug: 'career-launch',
      path: siteConfig.campaign.path,
      kind: 'campaign',
      title: intakeCopy.campaignTitle,
      metaDescription: intakeCopy.campaignMeta,
      h1: siteConfig.campaign.name,
      eyebrow: intakeCopy.campaignEyebrow,
      introAnswer: `${siteConfig.campaign.name} helps Bangladeshi students choose among AI Agent Builder, Full Stack Web Development with AI, and React Native + AI through a free live career class, practical outcome demos, WhatsApp counselling, and small-batch seat booking.`,
      schemaType: 'WebPage',
      sitemap: { priority: 0.9, changefreq: 'weekly' },
      sections: [
        {
          heading: 'The main offer',
          body: `This campaign is not a blind course-selling page. It starts with ${siteConfig.campaign.mainCta}, then helps each student choose the course path that fits their current skill, laptop access, English level, practice time, and career goal.`,
          items: [
            intakeCopy.batchStartItem,
            `Small cohort target: max ${enrollment.seats} students per main batch.`,
            intakeCopy.primaryCoursesItem,
            'Students can request the roadmap by sending AI, WEB, or AI CLASS on WhatsApp.',
          ],
        },
        {
          heading: 'Who should join AI Agent Builder',
          items: [
            'Students who want practical AI automation skills: n8n workflows, chatbots, WhatsApp agents, RAG basics, and portfolio-ready business use cases.',
            'Beginners who prefer a low-code first path but are ready to learn APIs, webhooks, JSON, and automation logic.',
            'Learners who want a competitive skill advantage for studies, projects, employment, startups, or optional freelancing later — not an income guarantee.',
          ],
        },
        {
          heading: 'Who should join Full Stack Web Development',
          items: [
            'Students who want coding foundations, React, Next.js, backend APIs, PostgreSQL, MongoDB, authentication, payments, deployment, and AI API integration.',
            'Beginners who are ready to practice consistently and build portfolio projects for jobs, internships, freelance profiles, or SaaS ideas.',
            'Learners who want a stronger long-term developer path instead of only learning tools.',
          ],
        },
        {
          heading: 'Who should join React Native + AI',
          items: [
            'Students who already know JavaScript/React basics and want to build iOS and Android apps from one codebase.',
            'Learners who want a published or demo-ready mobile app for interviews, personal products, or optional freelance work later.',
            'Full Stack graduates who want to add mobile as the next skill — not as an income guarantee.',
          ],
        },
        {
          heading: 'Seat booking and payment basics',
          items: [
            `Seat booking token: ${siteConfig.campaign.seatBookingToken}.`,
            `Installment support: ${siteConfig.campaign.installment}.`,
            'Payment options are discussed during counselling before final enrollment.',
            'No fake urgency: seat updates should be shown only from real admission status.',
          ],
        },
        {
          heading: 'Income disclaimer',
          tone: 'notice',
          body: siteConfig.trust.incomeDisclaimer,
        },
      ],
      faqs: [
        {
          question: 'Is this a free course?',
          answer:
            'No. The free part is the live career orientation and counselling. Paid course enrollment happens only after the student understands the roadmap, schedule, fee, and fit.',
        },
        {
          question: 'Which courses are the main focus?',
          answer:
            'This intake focuses on AI Agent Builder: From Beginner to Pro, Full Stack Web Development with AI Integration, and Mobile App Development with React Native + AI.',
        },
        {
          question: 'Can guardians join counselling?',
          answer:
            'Yes. Guardian counselling is welcome, especially for students who want help understanding course fit, time commitment, payment options, and realistic outcomes.',
        },
        {
          question: 'Does Skillo guarantee freelancing income?',
          answer: siteConfig.trust.incomeDisclaimer,
        },
      ],
      cta: {
        label: 'Join the free career orientation',
        href: siteConfig.campaign.orientationPath,
      },
      secondaryCtas: [
        { label: 'Send AI for roadmap', href: aiRoadmapUrl },
        { label: 'Send WEB for roadmap', href: webRoadmapUrl },
      ],
    },
    {
      slug: 'free-career-orientation',
      path: siteConfig.campaign.orientationPath,
      kind: 'campaign',
      title: intakeCopy.orientationTitle,
      metaDescription:
        'Join Skillo Academy’s free live career orientation class to compare AI Agent Builder and Full Stack Web Development with AI, see practical demos, and get WhatsApp counselling.',
      h1: 'Free Career Orientation Class',
      eyebrow: 'AI demo + web demo before admission',
      introAnswer:
        'The Skillo free career orientation class helps students understand whether AI Agent Builder or Full Stack Web Development with AI is the better first step by showing practical demos, prerequisites, project outcomes, and realistic expectations about skills, practice, and career paths.',
      schemaType: 'WebPage',
      sitemap: { priority: 0.88, changefreq: 'weekly' },
      sections: [
        {
          heading: 'What the AI demo shows',
          items: [
            'What an AI agent is and how it is different from only using ChatGPT.',
            'How an n8n workflow can collect a lead, update a sheet, and trigger a WhatsApp-style follow-up.',
            'How students can turn one useful automation demo into a portfolio proof for studies, interviews, projects, or future opportunities.',
          ],
        },
        {
          heading: 'What the full stack demo shows',
          items: [
            'How a modern web app combines frontend, backend, database, authentication, payment, deployment, and AI features.',
            'Why portfolio projects matter more than only finishing tutorials.',
            'How a 24-week roadmap of 72 classes can move a beginner from HTML/CSS to a deployed AI-powered project.',
          ],
        },
        {
          heading: 'Who should attend',
          items: [
            'Students after SSC, HSC, diploma, or university who want a practical tech path.',
            'Beginners confused between AI automation, web development, and basic computer foundations.',
            'Parents or guardians who want an honest explanation of course outcomes and limitations.',
          ],
        },
        {
          heading: 'After the class',
          items: [
            'Ask questions on WhatsApp using AI, WEB, or AI CLASS.',
            'Book a 10-minute counselling conversation if you need personal course guidance.',
            `Reserve a seat with ${siteConfig.campaign.seatBookingToken} only after you understand the roadmap and payment plan.`,
          ],
        },
        {
          heading: 'Income disclaimer',
          tone: 'notice',
          body: siteConfig.trust.incomeDisclaimer,
        },
      ],
      faqs: [
        {
          question: 'Do I need coding knowledge to attend?',
          answer:
            'No. The orientation is beginner-friendly and explains both the low-code AI automation path and the coding-heavy full stack path.',
        },
        {
          question: 'Will there be a recording?',
          answer:
            'Skillo can share class details and roadmap links through WhatsApp. Recording availability should be confirmed for each live session.',
        },
        {
          question: 'Can I ask which course is right for me?',
          answer: 'Yes. That is the main purpose of the orientation and follow-up counselling.',
        },
      ],
      cta: {
        label: 'Send AI CLASS on WhatsApp',
        href: aiClassUrl,
      },
      secondaryCtas: [
        { label: 'Read the AI roadmap', href: siteConfig.campaign.aiRoadmapPath },
        { label: 'Read the web roadmap', href: siteConfig.campaign.webRoadmapPath },
      ],
    },
    {
      slug: 'ai-agent-builder-90-day-roadmap',
      path: siteConfig.campaign.aiRoadmapPath,
      kind: 'roadmap',
      title: 'AI Agent Builder 90-Day Roadmap | Skillo Academy',
      metaDescription:
        'Read Skillo Academy’s AI Agent Builder 90-day roadmap for beginners: AI basics, n8n, APIs, RAG, WhatsApp agents, demos, and portfolio projects for studies and career growth.',
      h1: 'AI Agent Builder 90-Day Roadmap',
      eyebrow: '90-day AI learning plan',
      introAnswer:
        'The AI Agent Builder 90-Day Roadmap shows how a beginner can move from AI and automation fundamentals to n8n workflows, APIs, RAG concepts, WhatsApp-style agents, and portfolio demos — skills that can support studies, personal projects, employment, startups, or optional freelancing depending on effort and goals.',
      schemaType: 'WebPage',
      sitemap: { priority: 0.82, changefreq: 'weekly' },
      sections: [
        {
          heading: 'Days 1-30: Foundations',
          items: [
            'Understand LLMs, prompting, structured outputs, tokens, context windows, and safe AI use.',
            'Learn APIs, webhooks, JSON, forms, Google Sheets, Airtable-style databases, and basic error handling.',
            'Build small n8n automations such as form-to-sheet, email sorting, and notification workflows.',
          ],
        },
        {
          heading: 'Days 31-60: Useful AI workflows',
          items: [
            'Connect AI models to business tasks such as lead qualification, FAQ replies, summaries, and reports.',
            'Learn RAG basics for “chat with your documents” style use cases.',
            'Build local business demos for restaurants, clinics, coaching centers, shops, and service teams.',
          ],
        },
        {
          heading: 'Days 61-90: Portfolio and professional communication',
          items: [
            'Record 60-second demo videos of your best automations.',
            'Write clear case studies with scope, limitations, and handoff notes for each project.',
            'Practice presenting your work for class reviews, interviews, or optional opportunity channels — without promising income.',
          ],
        },
        {
          heading: 'What to avoid',
          items: [
            'Do not promise magic AI results without testing.',
            'Do not claim job placement or freelancing income as guaranteed outcomes.',
            'Do not present AI automation as a shortcut to easy money.',
          ],
        },
        {
          heading: 'Income disclaimer',
          tone: 'notice',
          body: siteConfig.trust.incomeDisclaimer,
        },
      ],
      faqs: [
        {
          question: 'Can I start this roadmap without coding?',
          answer:
            'Yes, you can start with visual workflows, but learning APIs, JSON, expressions, and basic JavaScript will make you stronger.',
        },
        {
          question: 'What should my first portfolio demo be?',
          answer:
            'A lead capture or booking automation is a good first demo because the business value is easy to explain in interviews, class projects, and portfolio reviews.',
        },
        {
          question: 'Does finishing this roadmap guarantee freelancing income?',
          answer: siteConfig.trust.incomeDisclaimer,
        },
      ],
      cta: {
        label: 'Send AI on WhatsApp',
        href: aiRoadmapUrl,
      },
      secondaryCtas: [
        { label: 'See the AI Agent course', href: '/courses/ai-agent-builder-beginner-to-pro' },
        { label: 'Join free orientation', href: siteConfig.campaign.orientationPath },
      ],
    },
    {
      slug: 'full-stack-developer-24-week-roadmap',
      path: siteConfig.campaign.webRoadmapPath,
      kind: 'roadmap',
      title: 'Full Stack Developer 24-Week Roadmap | Skillo Academy',
      metaDescription:
        'Read Skillo Academy’s Full Stack Developer 24-week roadmap for beginners: 72 live classes covering HTML, Git, CSS, Tailwind, JavaScript, TypeScript, React, Next.js, Node.js, PostgreSQL, MongoDB, testing, security, auth, payments, deployment and AI integration.',
      h1: 'Full Stack Developer 24-Week Roadmap',
      eyebrow: '24-week, 72-class plan',
      introAnswer:
        'The Full Stack Developer 24-Week Roadmap shows how a beginner moves, class by class, from how the web works and HTML, Git and GitHub, and CSS through JavaScript, TypeScript and React to Next.js, Node.js APIs, PostgreSQL and MongoDB, security, authentication, payments, AI features and a deployed capstone: 72 live classes, homework after every class and a check after every module.',
      schemaType: 'WebPage',
      sitemap: { priority: 0.82, changefreq: 'weekly' },
      sections: [
        {
          heading: 'Weeks 1-8: The web, HTML, Git, CSS and Tailwind (24 classes)',
          items: [
            'Week 1: what web development is and how the web works, setting up VS Code and Chrome, your first web page, HTML elements, attributes and structure, and text, lists and tables.',
            'Weeks 2-3: images, media and the page head, forms with validation, semantic and accessible HTML, a multi-page Sylhet Cafe website, and the terminal, Git and GitHub to publish it with GitHub Pages.',
            'Weeks 4-7: CSS from selectors, the cascade and the box model to Flexbox, Grid and mobile-first responsive design, ending with a deployed landing page.',
            'Week 8: Tailwind CSS, rebuilding the same landing page with utility classes.',
          ],
        },
        {
          heading: 'Weeks 9-14: JavaScript, APIs and TypeScript (18 classes)',
          items: [
            'JavaScript fundamentals: values, decisions, loops, functions, arrays and objects.',
            'Scope, closures, classes, modules and debugging, then the DOM, events, forms and localStorage.',
            'Promises, async/await, Fetch, JSON and REST APIs, then npm, Vite, ESLint, Prettier, team Git with pull requests, TypeScript and Zod.',
          ],
        },
        {
          heading: 'Weeks 15-18: React and Next.js (12 classes)',
          items: [
            'Components, props, state, lists, forms, effects and data fetching, custom hooks, React Router and TanStack Query, with a React project.',
            'The Next.js App Router: routing and layouts, Server and Client Components, data and caching, Server Actions and route handlers.',
          ],
        },
        {
          heading: 'Weeks 19-23: Backend, databases, security and payments (15 classes)',
          items: [
            'Node.js and Express REST APIs with validation, file uploads, configuration and tests.',
            'SQL and PostgreSQL, Prisma models, migrations and relations, and MongoDB with Mongoose, then a deployed API.',
            'Password hashing, sessions and JWT, roles, OWASP risks, and SSLCommerz, bKash and Stripe payments verified on the server.',
          ],
        },
        {
          heading: 'Week 24: Full-stack Next.js, AI and launch (3 classes)',
          items: [
            'Full-stack Next.js with authentication and a database.',
            'AI features with the OpenAI API: prompts, streaming responses and cost control.',
            'Deployment, CI and demo day, where you present your capstone project.',
          ],
        },
        {
          heading: 'How classes and checks work',
          items: [
            'Three live classes a week, two hours each. Each class has notes, practice and homework in the student portal, and opens there when your teacher starts it.',
            'Every module ends with a check that your teacher opens, and a final assessment includes one written design question that your teacher marks.',
          ],
        },
        {
          heading: 'Income disclaimer',
          tone: 'notice',
          body: siteConfig.trust.incomeDisclaimer,
        },
      ],
      faqs: [
        {
          question: 'Can I learn full stack in 24 weeks?',
          answer:
            'A focused beginner who attends the 72 classes and does the homework can build a strong portfolio foundation in 24 weeks. Mastery and paid results require continued practice after the course.',
        },
        {
          question: 'Do I need math?',
          answer:
            'Advanced math is not required for beginner web development, but logical thinking and consistent problem solving are important.',
        },
        {
          question: 'Does this roadmap guarantee freelancing income?',
          answer: siteConfig.trust.incomeDisclaimer,
        },
      ],
      cta: {
        label: 'Send WEB on WhatsApp',
        href: webRoadmapUrl,
      },
      secondaryCtas: [
        { label: 'See the Full Stack course', href: '/courses/full-stack-ai-web-development' },
        { label: 'Join free orientation', href: siteConfig.campaign.orientationPath },
      ],
    },
    {
      slug: 'referral-scholarship',
      path: siteConfig.campaign.referralScholarshipPath,
      kind: 'admission',
      title: 'Referral, Scholarship & Seat Booking | Skillo Academy',
      metaDescription: intakeCopy.admissionsMeta,
      h1: 'Referral, Scholarship & Seat Booking',
      eyebrow: 'Transparent admission support',
      introAnswer: intakeCopy.admissionsIntro,
      schemaType: 'WebPage',
      sitemap: { priority: 0.78, changefreq: 'weekly' },
      sections: [
        {
          heading: 'Referral benefit',
          items: [
            `Referrer reward: ${siteConfig.campaign.referral.referrerReward}.`,
            `Referred student benefit: ${siteConfig.campaign.referral.referredStudentDiscount}.`,
            siteConfig.campaign.referral.condition,
            'Referral benefits apply only to confirmed paid students, not casual inquiries.',
          ],
        },
        {
          heading: 'Founder scholarship',
          items: [
            siteConfig.campaign.scholarship.seatsPerCourse,
            ...siteConfig.campaign.scholarship.types,
            ...siteConfig.campaign.scholarship.requirements,
            'Scholarship decisions should be based on real applications and available seats.',
          ],
        },
        {
          heading: 'Seat booking and installments',
          items: [
            `Seat booking token: ${siteConfig.campaign.seatBookingToken}.`,
            `Installment option: ${siteConfig.campaign.installment}.`,
            'Students should complete counselling before seat booking if they are unsure which course fits them.',
            'Batch seat updates should be posted only from real enrollment status.',
          ],
        },
        {
          heading: 'No income guarantee',
          tone: 'notice',
          body: siteConfig.trust.incomeDisclaimer,
        },
      ],
      faqs: [
        {
          question: 'When is referral reward confirmed?',
          answer: siteConfig.campaign.referral.condition,
        },
        {
          question: 'How do I apply for scholarship?',
          answer:
            'Send your name, chosen course, background, and a 1-minute video explaining why you want to join. Skillo will review applications based on merit, need, and available seats.',
        },
        {
          question: 'Can I reserve a seat without counselling?',
          answer:
            'You can, but counselling is recommended if you are not sure whether AI Agent Builder or Full Stack Web Development is right for you.',
        },
        {
          question: 'Does a discount or scholarship guarantee earning?',
          answer: siteConfig.trust.incomeDisclaimer,
        },
      ],
      cta: {
        label: 'Ask about admission support',
        href: whatsappCounsellingUrl,
      },
      secondaryCtas: [
        { label: 'Join free orientation', href: siteConfig.campaign.orientationPath },
        { label: intakeCopy.seeCampaignCta, href: siteConfig.campaign.path },
      ],
    },
    {
      slug: 'student-projects',
      path: '/student-projects',
      kind: 'core',
      title: 'Student Project Paths | Skillo Academy',
      metaDescription:
        'See the types of portfolio projects Skillo Academy students build across AI automation, full stack web development, foundation computer skills, design, video editing, SEO, and digital marketing.',
      h1: 'Student Project Paths at Skillo Academy',
      eyebrow: 'Portfolio outcomes, not invented testimonials',
      introAnswer:
        'Skillo Academy courses are designed around portfolio-ready project paths. This page explains the types of AI automation, full stack web development, foundation, design, video editing, SEO, and marketing projects students work toward during live mentor-led training.',
      schemaType: 'CollectionPage',
      sitemap: { priority: 0.7, changefreq: 'monthly' },
      sections: [
        {
          heading: 'AI Agent Builder projects',
          items: [
            'Restaurant WhatsApp booking bot for menu questions, reservations, and confirmations.',
            'Clinic appointment AI agent for scheduling, reminders, and FAQ handling.',
            'Lead qualification workflow that captures form or ad leads and sends high-intent prospects to a human team.',
          ],
        },
        {
          heading: 'Full Stack Web Development projects',
          items: [
            'Responsive business website with contact form, WhatsApp CTA, and deployment.',
            'E-commerce or booking app with authentication, admin dashboard, and payment integration.',
            'AI-powered SaaS dashboard with a useful OpenAI API feature and a live demo URL.',
          ],
        },
        {
          heading: 'Foundation and creative project paths',
          items: [
            'Google Docs CV, Google Sheets tracker, Gmail workflow, and safe internet habits for foundation students.',
            'Figma UI screens, Canva/Photoshop design sets, and short-form video editing portfolios for creative tracks.',
            'SEO content briefs, local business landing pages, ad campaign plans, and reporting dashboards for marketing tracks.',
          ],
        },
        {
          heading: 'Proof policy',
          tone: 'notice',
          body: 'This page describes project paths and course outcomes. Real student testimonials, Google reviews, class photos, and project screenshots should be added only when the actual assets are available and approved.',
        },
      ],
      faqs: [
        {
          question: 'Will I build practical projects at Skillo?',
          answer:
            'Yes. Every Skillo course is structured around assignments and portfolio outcomes so students can show real work in class reviews, interviews, personal projects, or future opportunities.',
        },
        {
          question: 'Are these real student testimonials?',
          answer:
            'No. This page lists project paths and examples. Skillo should add real testimonials only after collecting verified student permission and assets.',
        },
        {
          question: 'Can I use project work for freelancing later?',
          answer:
            'Yes, project work is designed to become portfolio proof. Freelancing is optional. Client or income results still depend on practice, communication, consistency, market demand, and individual effort — and are never guaranteed.',
        },
      ],
      cta: {
        label: 'Explore courses that build these projects',
        href: '/courses',
      },
    },
    {
      slug: 'sylhet-freelancing-ai-web-development-courses',
      path: '/sylhet/freelancing-ai-web-development-courses',
      kind: 'local',
      title: 'AI, Web Development & Practical Tech Courses in Sylhet | Skillo Academy',
      metaDescription:
        'Skillo Academy offers live AI Agent, Full Stack Web Development, Basic Computer, design, digital marketing, and practical tech skill courses for students in Sylhet, Bangladesh.',
      h1: 'Live AI, Web Development & Practical Tech Courses in Sylhet',
      eyebrow: 'Local tech academy for Sylhet students',
      introAnswer:
        'Skillo Academy helps students in Sylhet learn practical AI, web development, automation, design, digital marketing, and career-ready technical skills through live mentor-led classes in Bangla + English, small batches, real projects, portfolio work, and WhatsApp support — without promising guaranteed freelancing income.',
      schemaType: 'WebPage',
      sitemap: { priority: 0.85, changefreq: 'weekly' },
      sections: [
        {
          heading: 'Courses for Sylhet students',
          items: [
            'AI Agent Builder for n8n automation, APIs, RAG, WhatsApp agents, voice agents, and portfolio-ready AI workflows.',
            'Full Stack Web Development with AI for React, Next.js, Node.js, PostgreSQL, MongoDB, auth, payments, deployment, and AI integration.',
            'Basic Computer & English for absolute beginners who need computer confidence before joining advanced tech courses.',
            'Design, video editing, SEO, and digital marketing tracks for students who want creative or marketing skill paths.',
          ],
        },
        {
          heading: 'Why local support matters',
          items: [
            'Students can contact Skillo directly through WhatsApp for counselling, admission, and batch guidance.',
            'The course examples include local business use cases such as restaurants, clinics, shops, real estate agencies, and service providers.',
            'Bangla + English teaching helps learners from Sylhet understand lessons while building technical English confidence.',
          ],
        },
        {
          heading: 'Address and contact',
          items: [
            `Location: ${siteConfig.contact.location}, Bangladesh.`,
            `WhatsApp: ${siteConfig.contact.phoneDisplay}.`,
            `Email: ${siteConfig.contact.email}.`,
          ],
        },
        {
          heading: 'Income disclaimer',
          tone: 'notice',
          body: siteConfig.trust.incomeDisclaimer,
        },
      ],
      faqs: [
        {
          question: 'Where is Skillo Academy located?',
          answer: `Skillo Academy is based in ${siteConfig.contact.location}, Bangladesh.`,
        },
        {
          question: 'Does Skillo teach freelancing courses in Sylhet?',
          answer:
            'Skillo teaches practical tech skills through AI automation, web development, foundation computer skills, design, video editing, SEO, and digital marketing. Freelancing can be one optional application of those skills later. Skillo does not guarantee income, clients, or fixed freelancing results.',
        },
        {
          question: 'Which Skillo course should a Sylhet beginner start with?',
          answer:
            'Absolute beginners should start with Basic Computer & English. Students with computer confidence can choose AI Agent Builder for automation skills or Full Stack Web Development with AI for coding and web apps.',
        },
      ],
      cta: {
        label: 'Ask for Sylhet admission guidance',
        href: whatsappCounsellingUrl,
      },
    },
    {
      slug: 'guides',
      path: '/guides',
      kind: 'guide',
      title: 'Skillo Guides | AI, Web Development & Career Skill Roadmaps',
      metaDescription:
        'Read Skillo Academy guides on AI agents, n8n automation, web development roadmaps, optional freelancing paths, and practical learning routes for Bangladeshi students.',
      h1: 'Skillo Guides for AI, Web Development and Career Skills',
      eyebrow: 'Answer pages for students',
      introAnswer:
        'Skillo Guides answer common student questions about AI Agent Building, n8n automation, Full Stack Web Development, practical career skills, and beginner-friendly tech roadmaps in Bangladesh — with honest expectations and no income guarantees.',
      schemaType: 'CollectionPage',
      sitemap: { priority: 0.75, changefreq: 'weekly' },
      sections: [
        {
          heading: 'Start here',
          items: [
            {
              label: 'What is an AI Agent?',
              href: '/guides/what-is-ai-agent',
            },
            {
              label: 'What is n8n automation?',
              href: '/guides/what-is-n8n-automation',
            },
            {
              label: 'AI Agent Builder vs Full Stack Development',
              href: '/guides/ai-agent-builder-vs-full-stack-development',
            },
            {
              label: 'Full Stack Web Development roadmap in Bangla',
              href: '/guides/full-stack-web-development-roadmap-bangla',
            },
          ],
        },
        {
          heading: 'Career and optional freelancing paths',
          items: [
            {
              label: 'Best freelancing skills for beginners in Bangladesh',
              href: '/guides/best-freelancing-skills-for-beginners-bangladesh',
            },
            {
              label: 'AI automation skills roadmap (optional freelancing path)',
              href: '/guides/ai-automation-freelancing-roadmap',
            },
            {
              label: 'How to start freelancing after learning web development',
              href: '/guides/how-to-start-freelancing-after-learning-web-development',
            },
            {
              label: 'How to learn web development in Bangla',
              href: '/guides/how-to-learn-web-development-in-bangla',
            },
          ],
        },
        {
          heading: 'Important disclaimer',
          tone: 'notice',
          body: siteConfig.trust.incomeDisclaimer,
        },
      ],
      faqs: [
        {
          question: 'Are these guides course pages?',
          answer:
            'No. These guides are answer pages that help students understand course choices, tools, roadmaps, and realistic career paths before enrolling.',
        },
        {
          question: 'Should I read the guides before counselling?',
          answer:
            'Yes. Reading the guides can make counselling faster because you will already understand the main course paths and tradeoffs.',
        },
      ],
      cta: {
        label: 'Get help choosing a course',
        href: whatsappCounsellingUrl,
      },
    },
    {
      slug: 'what-is-ai-agent',
      path: '/guides/what-is-ai-agent',
      kind: 'guide',
      title: 'What Is an AI Agent? Beginner Guide in Bangla Context | Skillo',
      metaDescription:
        'Learn what an AI agent is, how it uses tools, memory, APIs, and workflows, and why Bangladeshi students can learn AI agents for studies, projects, and career growth.',
      h1: 'What Is an AI Agent?',
      eyebrow: 'Beginner AI guide',
      introAnswer:
        'An AI agent is a software workflow that can use an AI model, tools, memory, APIs, and rules to complete tasks such as answering customers, booking appointments, qualifying leads, preparing reports, or updating business systems.',
      schemaType: 'WebPage',
      sitemap: { priority: 0.72, changefreq: 'monthly' },
      sections: [
        {
          heading: 'A simple explanation',
          body: 'A normal chatbot mostly replies to messages. An AI agent can also take actions through connected tools such as Google Sheets, Gmail, WhatsApp, calendars, CRMs, forms, databases, and webhooks.',
        },
        {
          heading: 'Examples for local businesses',
          items: [
            'A restaurant agent can answer menu questions and collect booking details through WhatsApp.',
            'A clinic agent can collect patient appointment requests and send reminders.',
            'A sales agent can qualify leads from ads and send high-intent prospects to a human team.',
          ],
        },
        {
          heading: 'What students should learn',
          items: [
            'Prompting, structured output, APIs, webhooks, JSON, and error handling.',
            'Workflow tools like n8n before moving into deeper coding tools like LangChain or LangGraph.',
            'How to turn one useful automation into a clear portfolio demo for studies, interviews, projects, or future opportunities.',
          ],
        },
      ],
      faqs: [
        {
          question: 'Is an AI agent the same as ChatGPT?',
          answer:
            'No. ChatGPT is an AI model interface. An AI agent usually connects a model to tools, data, memory, and workflows so it can do useful tasks.',
        },
        {
          question: 'Can beginners learn AI agents?',
          answer:
            'Yes. Beginners can start with visual workflow tools like n8n, then learn APIs and coding gradually.',
        },
      ],
      cta: {
        label: 'See the AI Agent Builder course',
        href: '/courses/ai-agent-builder-beginner-to-pro',
      },
    },
    {
      slug: 'what-is-n8n-automation',
      path: '/guides/what-is-n8n-automation',
      kind: 'guide',
      title: 'What Is n8n Automation? Beginner Guide | Skillo Academy',
      metaDescription:
        'Understand n8n automation, workflows, triggers, nodes, APIs, and why n8n is useful for students learning AI automation skills in Bangladesh.',
      h1: 'What Is n8n Automation?',
      eyebrow: 'Automation tool guide',
      introAnswer:
        'n8n is a workflow automation tool that connects apps, APIs, data, and AI models so repetitive business tasks can run automatically without building a full custom software product from scratch.',
      schemaType: 'WebPage',
      sitemap: { priority: 0.72, changefreq: 'monthly' },
      sections: [
        {
          heading: 'How n8n works',
          items: [
            'A trigger starts the workflow, such as a new form submission, message, email, or scheduled time.',
            'Nodes process the data, call APIs, update sheets, send messages, or ask an AI model for structured output.',
            'Error handling and testing make the workflow reliable enough for real business use.',
          ],
        },
        {
          heading: 'Useful project ideas',
          items: [
            'Lead capture from Facebook forms into Google Sheets or a CRM.',
            'WhatsApp notification flows for appointments and orders.',
            'AI-powered email sorting, report generation, and content repurposing.',
          ],
        },
        {
          heading: 'Why Skillo teaches it',
          body: 'n8n is beginner-friendly enough for visual learning but deep enough to connect with APIs, webhooks, databases, and AI systems. That makes it a practical bridge from no-code to pro-code automation for studies, projects, and career growth.',
        },
      ],
      faqs: [
        {
          question: 'Do I need coding for n8n?',
          answer:
            'You can start without coding, but learning APIs, JSON, expressions, and basic JavaScript makes you much stronger.',
        },
        {
          question: 'Can n8n be used with AI?',
          answer:
            'Yes. n8n can connect to OpenAI, Claude, Gemini, databases, forms, CRMs, and messaging tools to create AI automation workflows.',
        },
      ],
      cta: {
        label: 'Learn n8n in AI Agent Builder',
        href: '/courses/ai-agent-builder-beginner-to-pro',
      },
    },
    {
      slug: 'ai-agent-builder-vs-full-stack-development',
      path: '/guides/ai-agent-builder-vs-full-stack-development',
      kind: 'guide',
      title: 'AI Agent Builder vs Full Stack Development | Which Should You Learn?',
      metaDescription:
        'Compare AI Agent Builder and Full Stack Web Development for Bangladeshi students. Learn which path fits automation, coding, studies, jobs, projects, and long-term skill growth.',
      h1: 'AI Agent Builder vs Full Stack Development',
      eyebrow: 'Course comparison',
      introAnswer:
        'Choose AI Agent Builder if you want practical automation systems, chatbots, WhatsApp agents, and business workflow skills for studies, projects, and career growth. Choose Full Stack Development if you want stronger coding foundations and the ability to build complete websites, apps, SaaS products, and APIs. Neither path guarantees income.',
      schemaType: 'WebPage',
      sitemap: { priority: 0.74, changefreq: 'monthly' },
      sections: [
        {
          heading: 'Choose AI Agent Builder if',
          items: [
            'You want practical low-code AI automation skills for studies, projects, and career growth.',
            'You like connecting tools, workflows, APIs, WhatsApp, forms, and AI models.',
            'You want to solve business process problems for restaurants, clinics, shops, agencies, and service teams — and build demos you can show.',
          ],
        },
        {
          heading: 'Choose Full Stack Development if',
          items: [
            'You want to become a stronger software developer.',
            'You want to build full websites, dashboards, booking systems, e-commerce apps, SaaS products, and APIs.',
            'You are ready to practice coding consistently every week.',
          ],
        },
        {
          heading: 'The best combined path',
          body: 'Many students can start with Full Stack Web Development for coding foundations, then learn AI Agent Builder to add automation and AI services. Students who need faster low-code exposure can reverse the order.',
        },
        {
          heading: 'Income disclaimer',
          tone: 'notice',
          body: siteConfig.trust.incomeDisclaimer,
        },
      ],
      faqs: [
        {
          question: 'Which path is easier for beginners?',
          answer:
            'AI Agent Builder can feel faster at the start because visual workflow tools reduce coding pressure. Full Stack Development takes more practice but builds deeper engineering foundations.',
        },
        {
          question: 'Which path is better for jobs?',
          answer:
            'Full Stack Development has more traditional job titles. AI Agent Building is stronger for automation workflows, emerging AI roles, and optional freelancing — but neither path guarantees a job or income. Results depend on practice, portfolio, and market demand.',
        },
      ],
      cta: {
        label: 'Get course counselling',
        href: whatsappCounsellingUrl,
      },
    },
    {
      slug: 'how-to-learn-web-development-in-bangla',
      path: '/guides/how-to-learn-web-development-in-bangla',
      kind: 'guide',
      title: 'How to Learn Web Development in Bangla | Beginner Roadmap',
      metaDescription:
        'A Bangla-friendly roadmap for learning web development: HTML, CSS, Tailwind, JavaScript, TypeScript, React, Next.js, Node.js, PostgreSQL, MongoDB, deployment, and portfolio projects.',
      h1: 'How to Learn Web Development in Bangla',
      eyebrow: 'Beginner web roadmap',
      introAnswer:
        'To learn web development in Bangla, start with computer confidence, then learn HTML, CSS, Tailwind, JavaScript, TypeScript, React, Next.js, Node.js, PostgreSQL, MongoDB, Git, deployment, and portfolio projects step by step.',
      schemaType: 'WebPage',
      sitemap: { priority: 0.72, changefreq: 'monthly' },
      sections: [
        {
          heading: 'Step-by-step roadmap',
          items: [
            'Start with HTML, CSS, responsive layouts, and Tailwind CSS.',
            'Learn JavaScript basics, DOM, async code, Fetch API, and TypeScript.',
            'Build React apps, then move into Next.js, Node.js, Express, PostgreSQL, MongoDB, auth, payments, and deployment.',
          ],
        },
        {
          heading: 'How Bangla + English helps',
          body: 'Bangla explanations make concepts easier at the beginning. English terms are still important because documentation, errors, APIs, and global client communication mostly use English.',
        },
        {
          heading: 'Portfolio projects',
          items: [
            'Responsive business landing page.',
            'Dashboard or booking app with authentication.',
            'E-commerce or SaaS demo with payment and AI API integration.',
          ],
        },
      ],
      faqs: [
        {
          question: 'Can I learn web development only in Bangla?',
          answer:
            'You can learn concepts in Bangla, but you should also learn English technical terms because documentation, errors, and client work usually require them.',
        },
        {
          question: 'How long does it take to learn full stack development?',
          answer:
            'Our full stack course takes a focused beginner through 72 live classes over 24 weeks to a portfolio foundation, but becoming confident for jobs, projects, or optional freelancing depends on consistent practice and project quality — none of which are guaranteed outcomes.',
        },
      ],
      cta: {
        label: 'See the Full Stack course',
        href: '/courses/full-stack-ai-web-development',
      },
    },
    {
      slug: 'best-freelancing-skills-for-beginners-bangladesh',
      path: '/guides/best-freelancing-skills-for-beginners-bangladesh',
      kind: 'guide',
      title: 'Best Freelancing Skills for Beginners in Bangladesh | Skillo',
      metaDescription:
        'Explore beginner-friendly freelancing skills in Bangladesh including AI automation, web development, basic computer services, design, video editing, SEO, and digital marketing.',
      h1: 'Best Freelancing Skills for Beginners in Bangladesh',
      eyebrow: 'Freelancing roadmap',
      introAnswer:
        'Good freelancing skills for beginners in Bangladesh include basic computer services, web development, AI automation, graphic design, UI/UX design, video editing, SEO, content strategy, and digital marketing. The best choice depends on your computer skill, English level, laptop access, practice time, and interest.',
      schemaType: 'WebPage',
      sitemap: { priority: 0.72, changefreq: 'monthly' },
      sections: [
        {
          heading: 'Beginner-friendly paths',
          items: [
            'Basic computer and data entry services for absolute beginners.',
            'Graphic design, video editing, and social media support for creative learners.',
            'AI automation and web development for students willing to build deeper technical skills.',
          ],
        },
        {
          heading: 'How to choose',
          items: [
            'Choose Basic Computer & English if you still struggle with files, email, browser use, and English interfaces.',
            'Choose Full Stack Web Development if you want coding, apps, and long-term technical growth.',
            'Choose AI Agent Builder if you want practical automation skills that can later support optional freelancing, jobs, or projects.',
          ],
        },
        {
          heading: 'Income disclaimer',
          tone: 'notice',
          body: siteConfig.trust.incomeDisclaimer,
        },
      ],
      faqs: [
        {
          question: 'Can beginners start freelancing quickly?',
          answer:
            'Beginners can start small tasks quickly, but meaningful freelance income usually requires a clear skill, portfolio proof, communication practice, and consistent outreach.',
        },
        {
          question: 'Which freelancing skill pays the most?',
          answer:
            'Higher-paying skills usually require deeper expertise. AI automation, full stack development, paid ads, and advanced design can pay well, but results are not guaranteed.',
        },
      ],
      cta: {
        label: 'Get beginner course guidance',
        href: whatsappCounsellingUrl,
      },
    },
    {
      slug: 'full-stack-web-development-roadmap-bangla',
      path: '/guides/full-stack-web-development-roadmap-bangla',
      kind: 'guide',
      title: 'Full Stack Web Development Roadmap in Bangla | Skillo',
      metaDescription:
        'A practical full stack web development roadmap for Bangladeshi beginners: HTML, CSS, Tailwind, JavaScript, TypeScript, React, Next.js, Node.js, PostgreSQL, MongoDB, auth, payments, deployment, and AI.',
      h1: 'Full Stack Web Development Roadmap in Bangla',
      eyebrow: 'Coding roadmap',
      introAnswer:
        'A practical full stack roadmap starts with HTML, CSS, Tailwind, JavaScript, and TypeScript, then moves to React, Next.js, Node.js, Express, PostgreSQL, MongoDB, authentication, payments, deployment, and AI API integration through real portfolio projects.',
      schemaType: 'WebPage',
      sitemap: { priority: 0.72, changefreq: 'monthly' },
      sections: [
        {
          heading: 'Phase 1: Frontend basics',
          items: [
            'HTML semantic structure, forms, accessibility, and SEO basics.',
            'CSS, Flexbox, Grid, responsive design, and Tailwind CSS.',
            'JavaScript fundamentals, DOM, async/await, Fetch API, and TypeScript.',
          ],
        },
        {
          heading: 'Phase 2: Apps and backend',
          items: [
            'React components, state, hooks, routing, forms, and API integration.',
            'Next.js routing, rendering, deployment, and production app structure.',
            'Node.js, Express, PostgreSQL, MongoDB, authentication, payments, and file handling.',
          ],
        },
        {
          heading: 'Phase 3: Portfolio',
          items: [
            'Deploy every serious project publicly.',
            'Write short case studies explaining problem, stack, features, and screenshots.',
            'Add one AI-powered feature to stand out from basic portfolios.',
          ],
        },
      ],
      faqs: [
        {
          question: 'Should I learn MERN stack or Next.js?',
          answer:
            'Learn the fundamentals behind both. React, Node.js, APIs, databases, and deployment matter more than memorizing a stack label.',
        },
        {
          question: 'How many projects do I need?',
          answer:
            'Three polished projects are better than ten unfinished demos. A business website, full stack dashboard, and AI-powered app is a strong beginner set.',
        },
      ],
      cta: {
        label: 'Join Full Stack Web Development with AI',
        href: '/courses/full-stack-ai-web-development',
      },
    },
    {
      slug: 'ai-automation-freelancing-roadmap',
      path: '/guides/ai-automation-freelancing-roadmap',
      kind: 'guide',
      title: 'AI Automation Skills Roadmap | Optional Freelancing Path | Skillo',
      metaDescription:
        'Learn a practical AI automation skills roadmap using n8n, APIs, WhatsApp agents, RAG, portfolio demos, and optional freelancing steps — with honest expectations and no income guarantees.',
      h1: 'AI Automation Skills Roadmap (Optional Freelancing Path)',
      eyebrow: 'AI skills path',
      introAnswer:
        'An AI automation skills roadmap starts with one useful business workflow, then adds n8n, APIs, webhooks, structured AI output, WhatsApp or email automation, a demo video, and clear documentation. Freelancing is an optional later path — not a guaranteed outcome.',
      schemaType: 'WebPage',
      sitemap: { priority: 0.72, changefreq: 'monthly' },
      sections: [
        {
          heading: 'Roadmap',
          items: [
            'Learn workflow basics: triggers, nodes, conditions, APIs, JSON, and errors.',
            'Build three demos: lead capture, appointment booking, and reporting automation.',
            'Record demo videos and write simple case studies for each workflow.',
            'If you choose freelancing later, practice one niche first — restaurants, clinics, real estate, or agencies — with honest scoping.',
          ],
        },
        {
          heading: 'Portfolio project examples',
          items: [
            'WhatsApp booking automation demo.',
            'Lead qualification and Google Sheets or CRM handoff.',
            'Weekly reporting automation from forms, ads, or spreadsheets.',
          ],
        },
        {
          heading: 'Income disclaimer',
          tone: 'notice',
          body: siteConfig.trust.incomeDisclaimer,
        },
      ],
      faqs: [
        {
          question: 'Do I need a portfolio before pursuing opportunities?',
          answer:
            'Yes. Even simple demo workflows help teachers, interviewers, and potential clients understand what you can build. Portfolio quality matters more than promises.',
        },
        {
          question: 'Does Skillo guarantee freelancing income from this path?',
          answer: siteConfig.trust.incomeDisclaimer,
        },
      ],
      cta: {
        label: 'Build AI automation projects',
        href: '/courses/ai-agent-builder-beginner-to-pro',
      },
    },
    {
      slug: 'how-to-start-freelancing-after-learning-web-development',
      path: '/guides/how-to-start-freelancing-after-learning-web-development',
      kind: 'guide',
      title: 'How to Start Freelancing After Learning Web Development | Skillo',
      metaDescription:
        'Learn how beginner web developers can start freelancing with portfolio projects, business websites, clear packages, Upwork/Fiverr profiles, direct outreach, and realistic expectations.',
      h1: 'How to Start Freelancing After Learning Web Development',
      eyebrow: 'Web freelancing guide',
      introAnswer:
        'After learning web development, start freelancing by building a small portfolio, choosing one service package, creating a clear demo, writing simple case studies, setting up Upwork or Fiverr profiles, and pitching local businesses with a specific offer.',
      schemaType: 'WebPage',
      sitemap: { priority: 0.72, changefreq: 'monthly' },
      sections: [
        {
          heading: 'Your first portfolio set',
          items: [
            'A responsive business website.',
            'A booking or appointment app.',
            'An e-commerce or dashboard project with authentication and deployment.',
          ],
        },
        {
          heading: 'First service packages',
          items: [
            'Landing page for a local business.',
            'Five-page website with contact and WhatsApp CTA.',
            'Full stack booking or admin dashboard with maintenance option.',
          ],
        },
        {
          heading: 'Where to find clients',
          items: [
            'Direct local outreach to shops, clinics, restaurants, schools, and service businesses.',
            'Fiverr gigs with clear packages and demo links.',
            'Upwork proposals focused on one niche instead of generic web development.',
          ],
        },
        {
          heading: 'Income disclaimer',
          tone: 'notice',
          body: siteConfig.trust.incomeDisclaimer,
        },
      ],
      faqs: [
        {
          question: 'Can I freelance with only one project?',
          answer:
            'One strong project is better than none, but two or three polished demos make it easier to win trust.',
        },
        {
          question: 'Should I work free at first?',
          answer:
            'Avoid unlimited free work. A small discounted first project with clear scope is better than vague unpaid work.',
        },
      ],
      cta: {
        label: 'Learn full stack with portfolio support',
        href: '/courses/full-stack-ai-web-development',
      },
    },
  ]
}

export function getContentPages(now = new Date()) {
  return getStaticPages(now)
}

export function getStaticPagesForPrerender() {
  return getStaticPages(new Date(), { prerender: true })
}

export function getStaticPageByPath(pathname: string, now = new Date()) {
  const pages = getStaticPages(now)
  const normalizedPath = normalizeStaticPagePath(pathname)
  if (isLegacyCampaignPath(normalizedPath)) {
    return pages.find((page) => page.path === siteConfig.campaign.path)
  }
  if (normalizedPath === siteConfig.campaign.legacyWebRoadmapPath) {
    return pages.find((page) => page.path === siteConfig.campaign.webRoadmapPath)
  }
  return pages.find((page) => normalizeStaticPagePath(page.path) === normalizedPath)
}

export function getStaticPageItemKey(item: StaticPageItem) {
  return typeof item === 'string' ? item : `${item.label}:${item.href}`
}

export function formatStaticPageItemMarkdown(item: StaticPageItem) {
  return typeof item === 'string' ? item : `[${item.label}](${item.href})`
}

export function getStaticPageBreadcrumbs(page: StaticPage) {
  const home = { name: 'Home', url: '/' }
  const current = { name: page.h1, url: page.path }

  if (page.kind === 'guide' && page.path !== '/guides') {
    return [home, { name: 'Guides', url: '/guides' }, current]
  }

  if (page.kind === 'roadmap' || page.kind === 'admission') {
    return [home, { name: siteConfig.campaign.name, url: siteConfig.campaign.path }, current]
  }

  return [home, current]
}

function normalizeStaticPagePath(pathname: string) {
  const path = pathname.startsWith('/') ? pathname : `/${pathname}`
  return path.length > 1 ? path.replace(/\/+$/, '') : path
}
